import axios from 'axios';
import Config from '../../../config';
import Auth from '../../../auth';
import SavedSearchStorage from './SavedSearchStorage';
import {
  DEFAULT_PROFILE_TYPE,
  VISIBILITY_TYPES
} from '../utils/savedSearchConstants';
import {
  formatSavedSearchForAPI,
  parseSavedSearchFromAPI,
  sortSavedSearches
} from '../utils/savedSearchHelpers';
import { removeSystemFiltersFromConfig } from '../utils/filterManager';
import { SavedSearch } from '../types';

class SavedSearchService {
  storage: typeof SavedSearchStorage;

  constructor() {
    this.storage = SavedSearchStorage;
  }

  async loadSavedSearches(
    tableType: string,
    signal: AbortSignal | null = null,
    profileType: string = DEFAULT_PROFILE_TYPE,
    storageKeyPrefix: string = 'savedSearch-',
    configMode: string = 'table'
  ): Promise<SavedSearch[]> {
    try {
      const searches: SavedSearch[] = [];
      const isOpaque = configMode === 'opaque';

      const axiosConfig: Record<string, any> = signal ? { signal } : {};
      const companyId = this.storage.getCompanyId();
      const searchPrefix = isOpaque ? storageKeyPrefix : `${storageKeyPrefix}${tableType}-`;

      const userDataRes = await axios.get(
        `${Config.api}/odata/Company/Functions.GetUserDataStartsWith?name=${searchPrefix}&userid=${Auth.getUserId()}`,
        axiosConfig
      );

      const privateSearches: SavedSearch[] = [];
      for (const savedSearch of userDataRes.data.value) {
        try {
          const key = savedSearch.Key;

          if (!isOpaque) {
            const expectedCompanySuffix = `-company-${companyId}`;

            if (key.includes('-company-')) {
              if (!key.endsWith(expectedCompanySuffix)) {
                continue;
              }
            }
          }

          const searchData = JSON.parse(savedSearch.Value);
          if (searchData && typeof searchData === 'object') {
            if (searchData.id) {
              privateSearches.push({
                ...searchData,
                name: searchData.name || 'Untitled',
                configuration: isOpaque ? searchData.configuration : removeSystemFiltersFromConfig(searchData.configuration),
                visibility: VISIBILITY_TYPES.PRIVATE
              });
            } else {
              let name = key.substring(searchPrefix.length);
              if (!isOpaque) {
                if (name.includes('-company-')) {
                  name = name.substring(0, name.indexOf('-company-'));
                }
                name = name.replace(/_/g, ' ');
              }

              privateSearches.push({
                id: key,
                name: name || 'Untitled',
                tableType: tableType,
                configuration: isOpaque ? searchData : removeSystemFiltersFromConfig(searchData),
                visibility: VISIBILITY_TYPES.PRIVATE,
                isDefault: false,
                createdAt: searchData.createdAt,
                updatedAt: searchData.updatedAt
              });
            }
          }
        } catch (e) {
          console.error('Error parsing saved search:', e);
        }
      }

      searches.push(...privateSearches);

      const apiRes = await axios.get(
        `${Config.api}/odata/Company/VwImportExportProfiles?$filter=Type eq '${profileType}'`,
        axiosConfig
      );

      for (const apiSearch of apiRes.data.value) {
        const parsed = parseSavedSearchFromAPI(apiSearch, tableType);
        // In opaque mode, the API filter by profileType is sufficient scoping.
        // In table mode, additionally filter by tableType since multiple table types share 'SavedSearch'.
        if (isOpaque || parsed.tableType === tableType) {
          if (!isOpaque) {
            parsed.configuration = removeSystemFiltersFromConfig(parsed.configuration);
          }
          searches.push(parsed);
        }
      }

      const defaultId = await this.storage.getDefaultSearchId(tableType);
      if (defaultId) {
        const defaultSearch = searches.find(s => s.id === defaultId);
        if (defaultSearch) {
          defaultSearch.isDefault = true;
        }
      }

      // Deduplicate by name+visibility — same-named entries with different visibility
      // are legitimate (e.g. private "My Report" vs company-shared "My Report").
      // Only dedup within the same visibility level.
      const bestByKey = new Map<string, SavedSearch>();
      for (const search of searches) {
        const dedupKey = `${(search.name || '').toLowerCase()}::${search.visibility}`;
        const existing = bestByKey.get(dedupKey);
        if (!existing) {
          bestByKey.set(dedupKey, search);
          continue;
        }
        const existingIsDefault = existing.isDefault === true;
        const currentIsDefault = search.isDefault === true;
        if (currentIsDefault && !existingIsDefault) {
          bestByKey.set(dedupKey, search);
        } else if (!existingIsDefault && !currentIsDefault) {
          const existingTime = existing.updatedAt ? new Date(existing.updatedAt).getTime() : 0;
          const currentTime = search.updatedAt ? new Date(search.updatedAt).getTime() : 0;
          if (currentTime > existingTime) {
            bestByKey.set(dedupKey, search);
          }
        }
        // Otherwise keep existing (existing is default or both are default)
      }
      const winners = new Set(bestByKey.values());
      const deduplicated = searches.filter(s => winners.has(s));

      const sorted = sortSavedSearches(deduplicated);

      return sorted;
    } catch (error) {
      console.error('Error loading saved searches:', error);
      throw error;
    }
  }

  async saveSavedSearch(
    savedSearch: Partial<SavedSearch>,
    profileType: string = DEFAULT_PROFILE_TYPE,
    storageKeyPrefix: string = 'savedSearch-',
    configMode: string = 'table'
  ): Promise<SavedSearch> {
    try {
      const visibility = savedSearch.visibility || VISIBILITY_TYPES.PRIVATE;
      const isOpaque = configMode === 'opaque';

      if (visibility === VISIBILITY_TYPES.PRIVATE) {
        let searchId: string;

        if (savedSearch.id && typeof savedSearch.id === 'string') {
          // Use existing id (e.g. saving over a default profile's key)
          searchId = savedSearch.id;
        } else if (isOpaque) {
          searchId = `${storageKeyPrefix}${savedSearch.name as string}`;
        } else {
          const companyId = this.storage.getCompanyId();
          const baseId = `${storageKeyPrefix}${savedSearch.tableType}-${(savedSearch.name as string).replace(/\s+/g, '_')}`;
          searchId = `${baseId}-company-${companyId}`;
        }

        const searchData: SavedSearch = {
          ...savedSearch,
          visibility: visibility,
          id: searchId,
          name: savedSearch.name as string,
          tableType: savedSearch.tableType as string,
          configuration: savedSearch.configuration || {},
          isDefault: savedSearch.isDefault || false,
          createdAt: savedSearch.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        await this.storage.saveLocalSearch(searchId, searchData, isOpaque);

        return searchData;
      } else {
        const apiData = formatSavedSearchForAPI({
          ...savedSearch,
          visibility: visibility
        } as SavedSearch, profileType);
        await axios.put(
          `${Config.api}/odata/company/VwImportExportProfiles`,
          apiData
        );

        return {
          ...savedSearch,
          visibility: visibility,
          id: apiData.Id || `saved-search-${Date.now()}`,
          name: savedSearch.name as string,
          tableType: savedSearch.tableType as string,
          configuration: savedSearch.configuration || {},
          isDefault: savedSearch.isDefault || false,
          createdAt: savedSearch.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      }
    } catch (error) {
      console.error('Error saving search:', error);
      throw error;
    }
  }

  async updateSavedSearch(
    id: string | number,
    updates: Partial<SavedSearch>,
    profileType: string = DEFAULT_PROFILE_TYPE,
    storageKeyPrefix: string = 'savedSearch-',
    configMode: string = 'table',
    existingSearch?: SavedSearch
  ): Promise<SavedSearch> {
    try {
      const isOpaque = configMode === 'opaque';

      let existing = existingSearch;
      if (!existing) {
        const searches = await this.loadSavedSearches(updates.tableType as string, null, profileType, storageKeyPrefix, configMode);
        existing = searches.find(s => s.id === id);
      }

      if (!existing) {
        throw new Error('Saved search not found');
      }

      const updated: SavedSearch = { ...existing, ...updates };

      if (existing.visibility === VISIBILITY_TYPES.PRIVATE) {
        // Update local storage - save the full object
        const updatedSearch = {
          ...updated,
          updatedAt: new Date().toISOString()
        };
        await this.storage.saveLocalSearch(id as string, updatedSearch, isOpaque);
      } else {
        // Update via API
        const apiData = formatSavedSearchForAPI(updated, profileType);
        await axios.put(
          `${Config.api}/odata/company/VwImportExportProfiles`,
          apiData
        );
      }

      return updated;
    } catch (error) {
      console.error('Error updating saved search:', error);
      throw error;
    }
  }

  async deleteSavedSearch(
    id: string | number,
    tableType: string,
    profileType: string = DEFAULT_PROFILE_TYPE,
    storageKeyPrefix: string = 'savedSearch-',
    configMode: string = 'table',
    visibility: string = VISIBILITY_TYPES.PRIVATE,
    isDefault: boolean = false
  ): Promise<boolean> {
    try {
      const isOpaque = configMode === 'opaque';

      if (visibility === VISIBILITY_TYPES.PRIVATE) {
        // Delete from local storage
        await this.storage.deleteLocalSearch(id as string, isOpaque);
      } else {
        // Delete via API
        await axios.delete(
          `${Config.api}/odata/company/VwImportExportProfiles(${id})`
        );
      }

      // If this was the default, clear it
      if (isDefault) {
        await this.storage.setDefaultSearchId(tableType, null);
      }

      return true;
    } catch (error) {
      console.error('Error deleting saved search:', error);
      throw error;
    }
  }

  async setDefaultSearch(id: string | number | null, tableType: string): Promise<boolean> {
    try {
      // Save default preference
      await this.storage.setDefaultSearchId(tableType, id);

      return true;
    } catch (error) {
      console.error('Error setting default search:', error);
      throw error;
    }
  }

  async shareSavedSearch(
    id: string | number,
    shareType: string,
    tableType: string,
    profileType: string = DEFAULT_PROFILE_TYPE,
    storageKeyPrefix: string = 'savedSearch-',
    configMode: string = 'table',
    existingSearch?: SavedSearch
  ): Promise<SavedSearch> {
    try {
      const isOpaque = configMode === 'opaque';

      let search = existingSearch;
      if (!search) {
        const searches = await this.loadSavedSearches(tableType, null, profileType, storageKeyPrefix, configMode);
        search = searches.find(s => s.id === id);
      }

      if (!search) {
        throw new Error('Saved search not found');
      }

      const visibility = shareType === 'partners' ?
        VISIBILITY_TYPES.PARTNERS :
        VISIBILITY_TYPES.COMPANY;

      const updated: SavedSearch = { ...search, visibility };
      const apiData = formatSavedSearchForAPI(updated, profileType);

      // If this was a private search, remove from local storage
      if (search.visibility === VISIBILITY_TYPES.PRIVATE) {
        await this.storage.deleteLocalSearch(id as string, isOpaque);
        delete (apiData as any).Id; // Create new API entry
      }

      await axios.put(
        `${Config.api}/odata/company/VwImportExportProfiles`,
        apiData
      );

      // Return the updated object since API returns 204 No Content
      return {
        ...updated,
        id: apiData.Id || `saved-search-${Date.now()}`,
        updatedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error sharing saved search:', error);
      throw error;
    }
  }

  async duplicateSavedSearch(
    id: string | number,
    newName: string,
    tableType: string,
    profileType: string = DEFAULT_PROFILE_TYPE,
    storageKeyPrefix: string = 'savedSearch-',
    configMode: string = 'table'
  ): Promise<SavedSearch> {
    try {
      const searches = await this.loadSavedSearches(tableType, null, profileType, storageKeyPrefix, configMode);
      const original = searches.find(s => s.id === id);

      if (!original) {
        throw new Error('Saved search not found');
      }

      const duplicate: Partial<SavedSearch> = {
        ...original,
        id: undefined,
        name: newName,
        visibility: VISIBILITY_TYPES.PRIVATE,
        isDefault: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      return await this.saveSavedSearch(duplicate, profileType, storageKeyPrefix, configMode);
    } catch (error) {
      console.error('Error duplicating saved search:', error);
      throw error;
    }
  }

  async getDefaultSearch(tableType: string): Promise<SavedSearch | null> {
    try {
      const defaultId = await this.storage.getDefaultSearchId(tableType);

      if (!defaultId) {
        return null;
      }

      const searches = await this.loadSavedSearches(tableType);
      return searches.find(s => s.id === defaultId) || null;
    } catch (error) {
      console.warn('Error getting default search:', error);
      return null;
    }
  }

  async applySavedSearchFromURL(tableType: string): Promise<SavedSearch | null> {
    try {
      const searchId = this.storage.getSearchIdFromURL();

      if (!searchId) {
        return null;
      }

      const searches = await this.loadSavedSearches(tableType);
      return searches.find(s => s.id === searchId) || null;
    } catch (error) {
      console.warn('Error applying saved search from URL:', error);
      return null;
    }
  }
}

const savedSearchService = new SavedSearchService();
export default savedSearchService;
